/**
 * Community Profile GraphQL Queries & Mutations
 *
 * All GraphQL operations used by the community profile service.
 * Each export is a gql tagged template consumed by Apollo Client.
 *
 * Organized by domain:
 * - Profile fetching: GET_CURRENT_PROFILE, GET_PROFILE_BY_UID, GET_SETTINGS_PAGE_DATA
 * - Profile mutation: UPDATE_PROFILE
 * - Social: CREATE_FOLLOW, DELETE_FOLLOW, GET_FOLLOWERS, GET_FOLLOWING
 * - Tags / centers: GET_USER_TAG_OPTIONS, GET_USER_META_TAGS, GET_CANONICAL_CENTERS
 * - Playlists: GET_MY_PLAYLISTS, GET_MEMBER_PLAYLISTS_SHELL, GET_PLAYLISTS_WITH_ITEMS,
 *   SEARCH_MY_PLAYLISTS, GET_DISCOVER_PLAYLISTS, GET_FOLLOWED_PLAYLISTS
 * - Playlist mutations: UPDATE_PLAYLIST_VISIBILITY, DELETE_PLAYLIST,
 *   FOLLOW_PLAYLIST, UNFOLLOW_PLAYLIST
 * - Avatar: CREATE_PROFILE_PICTURE_UPLOAD, CREATE_PROFILE_PICTURE_CONFIRM,
 *   DELETE_PROFILE_PICTURE
 * - Activity: GET_COMMENT_COUNT, GET_COMMUNITY_LEADERBOARD
 * - Watch history: GET_USER_HISTORY
 *
 * @see fragments.ts for shared GraphQL fragments
 */

import { gql } from '@apollo/client'
import {
  CORE_USER_PROFILE_FIELDS,
  USER_PROFILE_SOCIAL_FIELDS,
  USER_PROFILE_SUMMARY_FIELDS,
  PLAYLIST_ITEM_CONTENT_FIELDS,
  MEMBER_PLAYLIST_SHELL_FIELDS,
  MEDIA_TILE_CONTENT_FEATURED,
  ORACLE_CONTENT_FIELDS,
} from './fragments'

export const GET_CURRENT_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileCurrent(
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfileCurrent(language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
      # Sage badge (profile-page only — deliberately NOT in the shared core
      # fragment; see the note there).
      isCircleFacilitator
    }
  }
`

/**
 * Lightweight viewer profile query — only the fields a page needs to
 * decide what to render before the heavy social/inProgress payload arrives.
 *
 * Used by CommunityMyLibraryPage to unblock its loading gate.  The full
 * GET_CURRENT_PROFILE fires in parallel for the Currently Watching carousel
 * (inProgress), which is rendered behind its own loading state.
 *
 * Keep this query lean — if a new consumer needs additional fields, prefer
 * a per-page query over expanding this one.
 */
export const GET_CURRENT_PROFILE_LIGHT = gql`
  query UserProfileCurrentLight($language: String) {
    userProfileCurrent(language: $language) {
      uid
      uuid
      displayName
      profileVisibility
      currentlyWatchingVisibility
      watchHistoryVisibility
      avatar {
        url
        small
        card
      }
    }
  }
`

export const GET_PROFILE_BY_UID = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileByUid(
    $uid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfileByUid(uid: $uid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

/**
 * Community-local hydration query used by `useTitleGroupHydration` for the
 * /library carousel tiles (system playlists' Watch Later / Favorites and
 * custom playlists' shell-rendered tiles, plus Currently Watching's
 * difficulty-pill enrichment).
 *
 * Why this exists (CO-497): the shared `GET_CONTENT` query in
 * `lib/services/content/query.js` gates the load-bearing series scalars
 * (`contentType`, `subType`, `displayType`, `studio`, `contentAnalytic`)
 * behind `@skip(if: $limitedData)`, and the legacy hydration hook was passing
 * `limitedData: true`. That stripped the series shape from the hydrated
 * payload, causing `buildEpisodeOfSeriesTile`'s `series.contentType`
 * guard to bail and the tiles to flip from series-shaped to plain video
 * on the second paint.
 *
 * This query mirrors the Content-side selection the tile builders
 * (`buildTileData`, `buildEpisodeOfSeriesTile`) actually read, with NO
 * `@skip` directives on the series scalars. Includes:
 *   - the load-bearing series projection (contentType, subType,
 *     displayType, studio, contentAnalytic, etc.)
 *   - `featureMedia.mediaAssets.aiMetadata.learnerLevel` — the perf
 *     reason for hydration in the first place (Continue Watching
 *     difficulty pill)
 *   - `contentAssetReferences` (worksheet thumbnails)
 *
 * Cache policy: `cache-and-network` (set by the consumer). Same network
 * shape as `GET_CONTENT` so Apollo's normalized `Content:<id>` entries
 * stay compatible with member-home / discover tiles.
 *
 * Trade-off: a community-owned near-duplicate of `GET_CONTENT`'s tile-
 * shaped subset. The Content team's eventual fix (drop @skip on the
 * five series scalars in the shared query) would let us delete this
 * query — kept narrow on purpose so the diff is small if/when that
 * happens.
 */
export const GET_CONTENT_FOR_TILE_HYDRATION = gql`
  query CommunityContentForTileHydration(
    $titleGroupIds: [String]!,
    $language: [String]!,
    $imageType: [String]!,
    $learnerLevelLanguage: String!,
  ) {
    contentList(
      where: { titleGroupId: { in: $titleGroupIds } }
      language: $language
    ) {
      content {
        id
        contentId
        titleGroupId
        contentType
        title
        path
        shortDescription
        episodeNumber
        seasonNumber
        instructor
        cast
        primaryLanguage
        isNew
        displayType
        subType
        studio
        contentAnalytic {
          votesUpCountTotal
          votesCountTotal
          commentsCountTotal
          thumbPopularityMappingId
        }
        featureMedia {
          duration
          mediaId
          mediaAssets {
            language
            mediaReferenceType
            aiMetadata(language: $learnerLevelLanguage) {
              learnerLevel {
                level
              }
            }
          }
          userMediaPosition {
            position
          }
          contentPolicy {
            accessCheck {
              allowed
            }
          }
        }
        imageContentRelationships(
          language: $language
          types: $imageType
        ) {
          type
          image {
            url
          }
        }
        contentAssetReferences(contentAssetReferences: { type: "worksheet" }) {
          asset {
            originalFileName
            language
            renderUrl
            mimeType
            uuid
          }
          contentAssetReferenceType {
            type
          }
        }
        references(types: ["genres"], typesByTypeTag: true) {
          contentReferenceType { type }
          contentTo(language: $language) {
            title
            path(format: RELATIVE)
            contentType
            contentId
            language
          }
        }
        contentSeries(language: $language) {
          id
          contentId
          contentType
          title
          path(format: RELATIVE)
          shortDescription
          subType
          displayType
          studio
          contentAnalytic {
            votesUpCountTotal
            votesCountTotal
            commentsCountTotal
            thumbPopularityMappingId
          }
          imageContentRelationships(
            language: $language
            types: $imageType
          ) {
            type
            image {
              url
            }
          }
          references(types: ["genres"], typesByTypeTag: true) {
            contentReferenceType { type }
            contentTo(language: $language) {
              title
              path(format: RELATIVE)
              contentType
              contentId
              language
            }
          }
        }
        contentFeatured(language: $language, content: [{ publishedState: PUBLISHED_NOW }]) {
          featuredType
          content {
            contentId
            contentType
            title
            path(format: RELATIVE)
            episodeNumber
            seasonNumber
            contentAnalytic {
              votesUpCountTotal
              votesCountTotal
              commentsCountTotal
              thumbPopularityMappingId
            }
            featureMedia {
              duration
              mediaId
              userMediaPosition {
                position
              }
              contentPolicy {
                accessCheck {
                  allowed
                }
              }
              mediaAssets {
                language
                mediaReferenceType
                aiMetadata(language: $learnerLevelLanguage) {
                  learnerLevel {
                    level
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

/**
 * In Progress carousel — fires after the main profile query lands.
 *
 * Splits the heavy `inProgress` selection out of GET_CURRENT_PROFILE /
 * GET_PROFILE_BY_UID for the profile page. The main profile query renders
 * scalars + system-playlist meta fast (passes includeMemberFields=false,
 * includeWatchHistory=false to skip the heavy arrays); the carousel fills
 * behind its own loading state when this query lands.
 *
 * Uses `userProfileByUid` for both own + member-viewing-other cases —
 * server serves the requesting user's own data when uid=loggedInUid.
 *
 * Anonymous viewers cannot see inProgress (auth-gated), so the consumer
 * skips this query for the public-viewer path.
 */
export const GET_PROFILE_IN_PROGRESS_CAROUSEL = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query UserProfileInProgressCarousel(
    $uuid: ID!
    $contentLanguage: [String]
  ) {
    userProfileByUuid(uuid: $uuid) {
      uuid
      inProgress(limit: 24) {
        mediaId
        uid
        position
        lastPlayed
        watchedCount
        hiddenByUser
        hiddenFromContinue
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
    }
  }
`

/**
 * Watch History carousel — fires after the main profile query lands.
 *
 * Mirrors GET_PROFILE_IN_PROGRESS_CAROUSEL but selects `watchHistory`
 * instead of `inProgress`. Same auth gating (anonymous viewers skip).
 */
export const GET_PROFILE_WATCH_HISTORY_CAROUSEL = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query UserProfileWatchHistoryCarousel(
    $uuid: ID!
    $contentLanguage: [String]
  ) {
    userProfileByUuid(uuid: $uuid) {
      uuid
      watchHistory(limit: 24) {
        mediaId
        uid
        position
        lastPlayed
        watchedSum
        hiddenByUser
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
    }
  }
`

/**
 * In-progress items with a higher limit for the full-page view.
 *
 * The shared USER_PROFILE_SOCIAL_FIELDS fragment caps inProgress at 10 items
 * (enough for the profile carousel preview). This standalone query uses a
 * larger limit so the /currently-watching page can show all in-progress series.
 *
 * The field selection mirrors USER_PROFILE_SOCIAL_FIELDS.inProgress exactly;
 * keep them in sync when the fragment changes.
 */
export const GET_IN_PROGRESS_FULL = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query InProgressFull($language: String, $contentLanguage: [String]) {
    userProfileCurrent(language: $language) {
      uuid
      uid
      inProgress(limit: 24) {
        mediaId
        uid
        position
        lastPlayed
        watchedCount
        hiddenByUser
        hiddenFromContinue
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
    }
  }
`

/**
 * In-progress items via userMediaPositionList with backend filtering.
 *
 * Replaces GET_IN_PROGRESS_FULL (which used the unfiltered inProgress federated
 * field). Backend filters handle: hiddenByUser, lastPlayed > 0, watchedSum >= 15s.
 * Client-side useInProgressEnrichment adds the 95%-completion threshold.
 */
export const GET_IN_PROGRESS_LIST = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query GetInProgressList(
    $limit: Int
    $contentLanguage: [String]
  ) {
    userMediaPositionList(
      userCurrent: true
      publishedMediaOnly: true
      limit: $limit
      order: [["lastPlayed", "DESC"]]
      where: {
        hiddenByUser: { eq: false }
        lastPlayed: { gt: "0" }
        watchedSum: { gte: "15" }
      }
    ) {
      userMediaPositions {
        mediaId
        position
        lastPlayed
        watchedSum
        hiddenByUser
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
      count
    }
  }
`

export const GET_PUBLIC_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfilePublic(
    $uid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfilePublic(uid: $uid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

export const GET_PROFILE_BY_UUID = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileByUuid(
    $uuid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfileByUuid(uuid: $uuid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
      # Sage badge (profile-page only — see the note in CoreUserProfileFields).
      isCircleFacilitator
    }
  }
`

export const GET_PUBLIC_PROFILE_BY_UUID = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfilePublicByUuid(
    $uuid: ID!
    $language: String
    $contentLanguage: [String]
    $includeMemberFields: Boolean!
    $includeProfileActivity: Boolean!
    $includeWatchHistory: Boolean!
  ) {
    userProfilePublicByUuid(uuid: $uuid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
      # Sage badge (profile-page only — see the note in CoreUserProfileFields).
      isCircleFacilitator
    }
  }
`

export const UPDATE_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  mutation UserProfileUpdate($input: UserProfileUpdateInput!, $language: String) {
    userProfileUpdate(input: $input) {
      profile {
        ...CoreUserProfileFields
        # Cascade reads: when input includes profileVisibility=private, brooklyn
        # cascades favorites/watchLater visibility transactionally. Selecting
        # them here lets Apollo write the new values to the Playlist:<id>
        # entities in cache without a refetch.
        favorites {
          id
          visibility
        }
        watchLater {
          id
          visibility
        }
      }
    }
  }
`

/**
 * Reserved for future user-follow feature.
 * Not currently used in any component — kept so the mutation is ready
 * when user-to-user following is implemented.
 */
export const CREATE_FOLLOW = gql`
  mutation UserProfileFollow($uid: ID!) {
    userProfileFollow(uid: $uid) {
      profile {
        uid
        uuid
        followerCount
        followingCount
        viewerContext {
          isOwner
          isFollowing
          isFollowedBy
          visibilityLevel
        }
      }
    }
  }
`

/**
 * Reserved for future user-follow feature.
 * Not currently used in any component — kept so the mutation is ready
 * when user-to-user following is implemented.
 */
export const DELETE_FOLLOW = gql`
  mutation UserProfileUnfollow($uid: ID!) {
    userProfileUnfollow(uid: $uid) {
      profile {
        uid
        uuid
        followerCount
        followingCount
        viewerContext {
          isOwner
          isFollowing
          isFollowedBy
          visibilityLevel
        }
      }
    }
  }
`

export const GET_FOLLOWERS = gql`
  ${USER_PROFILE_SUMMARY_FIELDS}
  query UserProfileFollowers($uid: ID!, $first: Int = 20, $after: String, $searchQuery: String) {
    userProfileFollowers(uid: $uid, first: $first, after: $after, searchQuery: $searchQuery) {
      edges {
        node {
          ...UserProfileSummaryFields
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_FOLLOWING = gql`
  ${USER_PROFILE_SUMMARY_FIELDS}
  query UserProfileFollowing($uid: ID!, $first: Int = 20, $after: String, $searchQuery: String) {
    userProfileFollowing(uid: $uid, first: $first, after: $after, searchQuery: $searchQuery) {
      edges {
        node {
          ...UserProfileSummaryFields
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_SETTINGS_PAGE_DATA = gql`
  ${CORE_USER_PROFILE_FIELDS}
  query SettingsPageData($language: String) {
    userProfileCurrent(language: $language) {
      ...CoreUserProfileFields
    }
    userTagOptionsList(language: $language) {
      id
      tag
      category
      label
    }
  }
`

export const GET_USER_TAG_OPTIONS = gql`
  query UserTagOptionsList($language: String) {
    userTagOptionsList(language: $language) {
      id
      tag
      category
      label
    }
  }
`

/**
 * Centers-bundle tags linked from topic-center (EN rows).
 * Aligns with userProfileUpdate.centerContentIds.
 */
// Topic tag labels (centers / canonical interests) come pre-translated from
// the backend. Caller must pass the user's primary app language with English
// fallback (e.g. `[language, 'en']`), mirroring GET_PLAYLIST_BY_ID and other
// content queries. Without a real language variable here, every locale would
// see the English `title` (the prior `["en"]` hardcode was the source of the
// non-translating community-profile-topic-tag labels).
export const GET_CANONICAL_CENTERS = gql`
  query CanonicalCenters($language: [String]!) {
    contentList(
      where: { contentType: { eq: "tag" }, bundle: { eq: "centers" } }
      contentReferencesFrom: [{ type: "topic-center" }]
      language: $language
      limit: 100
    ) {
      content {
        contentId
        title
      }
      count
    }
  }
`

export const GET_MY_PLAYLISTS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query MyPlaylists($language: [String]!) {
    playlistList(userCurrent: true, includePrivate: true, limit: 100) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        followerCount
        description
        playlistItems {
          ...PlaylistItemContentFields
        }
      }
    }
  }
`

// Metadata-only variant of GET_MY_PLAYLISTS — NO playlistItems, so it costs a
// fraction of the full query. Used to resolve a system playlist's id from its
// playlistType (slug routes /favorites, /watch-later) since PlaylistWhereInput
// can't filter by playlistType; the resolved id then feeds
// GET_PLAYLISTS_WITH_ITEMS so the full-page view fetches content for ONLY the
// playlist being viewed instead of every playlist the user owns.
// `limit: 200` is a client-supplied page size (the schema constrains `limit`
// to 0–1000; `offset` exists but is unused here). It's deliberately higher
// than the `limit: 100` used by GET_MY_PLAYLISTS / the shell query: this
// metadata-only query is cheap (no playlistItems), and the server caps a
// member's OWNED playlists at 99 (`LIMIT_USER_PLAYLISTS`, gaia-brooklyn-api),
// so 200 always returns the complete owned set in one page — the two counts
// derived from it (the My Playlists tab total `myPlaylistsTotalCount` and the
// profile "Sharing N" privacy summary `sharedOnProfileCount`) are exact, and
// whole-library reads (reorder) are never truncated.
export const GET_MY_PLAYLISTS_META = gql`
  query MyPlaylistsMeta {
    playlistList(userCurrent: true, includePrivate: true, limit: 200) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        playlistIndex
        createdAt
        updatedAt
      }
    }
  }
`

// Shell shape for the member's own playlists: playlistItems carry only
// { id, contentId, contentType, playlistPosition }; full Content fields are
// hydrated separately via GET_CONTENT_FOR_TILE_HYDRATION (cache-and-network)
// at the carousel/row layer.
// Shared by the paged list AND the search query so rows hydrate identically.
export const MY_PLAYLIST_SHELL_FIELDS = gql`
  fragment MyPlaylistShellFields on Playlist {
    id
    playlistName
    playlistType
    visibility
    itemCount
    followerCount
    description
    playlistIndex
    createdAt
    updatedAt
    playlistItems {
      id
      contentId
      titleGroupId
      contentType
      playlistPosition
    }
  }
`

// cache-and-network: user-scoped, lists which playlists I own. Limit 24 pairs
// with the offset pagination wired in useProfilePlaylists.
//
// `$order` carries the member's chosen sort. OMITTING the variable (never
// sending `order: null` — GraphQL treats explicit null as provided) gives the
// server default for a single-member query — [['playlistIndex','DESC'],
// ['createdAt','DESC']], i.e. their manual order — so `manual` omits the key.
// Sorting MUST happen here rather than client-side: the list is paged, so a
// client sort would only order the rows already loaded. `toPlaylistListOrder`
// in playlistSort.ts owns the mapping.
//
// `$where` carries the active sharing filter, arriving as `id: { in: [...] }`
// (resolved client-side from META) because
// `playlistList(visibility:)` is ignored on a userCurrent query — see
// playlistFilters.ts. Both go through the SAME paged query, so filtering keeps
// server ordering and paging rather than slicing an already-loaded page.
//
// Apollo has no typePolicy for `playlistList`, so each distinct `order` gets
// its own cache field key — which is what we want. Do NOT add keyArgs or a
// merge function; paging goes through updateQuery in useApolloListPagination.
export const GET_MY_PLAYLISTS_SHELL = gql`
  ${MY_PLAYLIST_SHELL_FIELDS}
  query MyPlaylistsShell(
    $limit: Int = 24
    $offset: Int = 0
    $order: [[String]]
    $where: PlaylistWhereInput
  ) {
    playlistList(
      userCurrent: true
      includePrivate: true
      limit: $limit
      offset: $offset
      order: $order
      where: $where
    ) {
      playlists {
        ...MyPlaylistShellFields
      }
    }
  }
`

// Server-side name search across the member's WHOLE library (not just the
// paged-in shells). `searchTerm` is a case-insensitive partial name match on
// the backend; limited to 100 matches (the resolver's per-request cap). Runs
// only while a search is active. Same shell shape as GET_MY_PLAYLISTS_SHELL so
// matched rows hydrate their content the same way.
export const GET_MY_PLAYLISTS_SEARCH = gql`
  ${MY_PLAYLIST_SHELL_FIELDS}
  query MyPlaylistsSearch(
    $searchTerm: String!
    $limit: Int = 100
    $where: PlaylistWhereInput
  ) {
    playlistList(
      userCurrent: true
      includePrivate: true
      searchTerm: $searchTerm
      limit: $limit
      where: $where
    ) {
      playlists {
        ...MyPlaylistShellFields
      }
    }
  }
`

// Full shells for a specific set of the member's own playlists, by id. Used to
// resolve the system playlists (Watch Later = `default`, Favorites) reliably:
// they sort to the tail of the owned list, so the paged shell query rarely
// includes them. We get their ids from GET_MY_PLAYLISTS_META (all owned
// playlists) and fetch their FULL shells here (META omits `playlistItems`, which
// the rows need to hydrate). Same shell shape as the paged list.
export const GET_MY_PLAYLISTS_SHELL_BY_ID = gql`
  ${MY_PLAYLIST_SHELL_FIELDS}
  query MyPlaylistsShellById($ids: [ID]!) {
    playlistList(where: { id: { in: $ids } }) {
      playlists {
        ...MyPlaylistShellFields
      }
    }
  }
`

export const GET_PLAYLISTS_WITH_ITEMS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query PlaylistsWithItems($playlistIds: [ID]!, $language: [String]!) {
    playlistList(where: { id: { in: $playlistIds } }) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        playlistItems {
          ...PlaylistItemContentFields
        }
      }
    }
  }
`

export const GET_PLAYLIST_BY_ID = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query PlaylistById($playlistId: ID!, $language: [String]!) {
    playlistList(where: { id: { eq: $playlistId } }) {
      playlists {
        id
        uid
        playlistName
        playlistType
        visibility
        description
        itemCount
        followerCount
        isFollowing
        userProfile {
          uuid
          displayName
          avatar {
            url
            small
            card
          }
        }
        playlistItems {
          ...PlaylistItemContentFields
          content(language: $language) {
            featureMedia {
              contentPolicy {
                shareAllowed
                accessCheck {
                  countryAllowed
                }
              }
            }
          }
        }
      }
    }
  }
`

export const GET_DISCOVER_PLAYLIST_DETAILS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query DiscoverPlaylistDetails($playlistIds: [ID]!, $language: [String]!) {
    playlistList(where: { id: { in: $playlistIds } }) {
      playlists {
        id
        uid
        playlistName
        playlistType
        visibility
        description
        itemCount
        followerCount
        isFollowing
        userProfile {
          uuid
          displayName
          avatar {
            url
            small
            card
          }
        }
        playlistItems {
          ...PlaylistItemContentFields
        }
      }
    }
  }
`

export const GET_USER_META_TAGS = gql`
  query ProfileMetaTags {
    userMetaTagList(userCurrent: true) {
      userMetaTags {
        id
        name
        tagType
        viewCount
        viewSeconds
        weight
      }
    }
  }
`

export const GET_USER_HISTORY = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query GetUserHistory(
    $limit: Int
    $offset: Int
    $contentLanguage: [String]
  ) {
    userMediaPositionList(
      userCurrent: true
      publishedMediaOnly: true
      limit: $limit
      offset: $offset
      order: [["lastPlayed", "DESC"]]
      where: {
        hiddenByUser: { eq: false }
        lastPlayed: { gt: "0" }
        watchedSum: { gte: "15" }
      }
    ) {
      userMediaPositions {
        mediaId
        position
        lastPlayed
        watchedSum
        hiddenByUser
        media {
          mediaId
          duration
          contentFeatured(language: $contentLanguage, limit: 1) {
            ...MediaTileContentFeatured
          }
        }
      }
      count
    }
  }
`

export const CREATE_PROFILE_PICTURE_UPLOAD = gql`
  mutation RequestProfilePictureUpload($fileExtension: String!) {
    requestProfilePictureUpload(fileExtension: $fileExtension) {
      uploadUrl
      imageUuid
      expiresAt
    }
  }
`

export const CREATE_PROFILE_PICTURE_CONFIRM = gql`
  mutation ConfirmProfilePictureUpload($imageUuid: ID!, $imageExtension: String!) {
    confirmProfilePictureUpload(imageUuid: $imageUuid, imageExtension: $imageExtension) {
      success
      profilePicture {
        url
        small
        card
      }
    }
  }
`

export const DELETE_PROFILE_PICTURE = gql`
  mutation DeleteProfilePicture {
    deleteProfilePicture {
      success
    }
  }
`

export const UPDATE_PLAYLIST_VISIBILITY = gql`
  mutation UpdatePlaylistVisibility($playlistId: ID!, $visibility: PlaylistVisibility!) {
    updatePlaylistVisibility(playlistId: $playlistId, visibility: $visibility) {
      playlist {
        id
        visibility
      }
    }
  }
`

// Reorders the member's CUSTOM playlists. Pass the full ordered array of
// custom playlist IDs (top first); system playlists are ignored, omitted
// customs append at the bottom. The response returns the full custom set in
// its new order — Apollo normalizes by `id`, so the My-Library list (sorted by
// `playlistIndex` DESC) re-renders from cache. See useReorderMyPlaylists.
export const REORDER_PLAYLISTS = gql`
  mutation ReorderPlaylists($playlistIds: [ID!]!) {
    reorderPlaylists(playlistIds: $playlistIds) {
      playlists {
        id
        playlistIndex
        playlistName
        playlistType
      }
    }
  }
`

export const DELETE_PLAYLIST = gql`
  mutation DeletePlaylist($playlistType: String!) {
    playlistDelete(playlist: { playlistType: $playlistType }) {
      playlist {
        id
        uid
        playlistType
        playlistName
      }
    }
  }
`

// IMPORTANT: keep this response shape symmetric with `UNFOLLOW_PLAYLIST` —
// just the fields the React handlers read (`followerCount`, `isFollowing`).
// Returning a richer Playlist (especially nested `userInfo`) used to make
// Apollo replace the embedded `userInfo` on the normalized `Playlist:<id>`
// cache entry, drop `userInfo.avatar`, and trigger an automatic
// `PlaylistById` refetch on the share page (huge payload, blocked the UI
// until it completed). Symmetric minimal shape avoids that cache thrash and
// matches the pattern Discover/Following already use successfully.
export const FOLLOW_PLAYLIST = gql`
  mutation FollowPlaylist($playlistId: ID!) {
    followPlaylist(playlistId: $playlistId) {
      id
      playlistName
      followerCount
      isFollowing
    }
  }
`

export const UNFOLLOW_PLAYLIST = gql`
  mutation UnfollowPlaylist($playlistId: ID!) {
    unfollowPlaylist(playlistId: $playlistId) {
      id
      playlistName
      followerCount
      isFollowing
    }
  }
`

export const ADD_PLAYLIST_ITEM = gql`
  mutation AddPlaylistItem(
    $contentId: Int!
    $contentType: String!
    $playlistType: String!
    $playlistId: ID
  ) {
    playlistCreatePlaylistItem(
      playlistItem: { contentId: $contentId, contentType: $contentType }
      playlist: { id: $playlistId, playlistType: $playlistType }
      createPlaylistIfNotExist: true
    ) {
      playlistItem {
        id
        playlistId
        contentType
        contentId
        playlistPosition
      }
    }
  }
`

export const REMOVE_PLAYLIST_ITEM = gql`
  mutation RemovePlaylistItem(
    $contentId: Int!
    $contentType: String!
    $playlistId: ID!
    $playlistType: String!
  ) {
    playlistDeletePlaylistItem(
      playlistItem: { contentId: $contentId, contentType: $contentType }
      playlist: { id: $playlistId, playlistType: $playlistType }
    ) {
      playlistItem {
        id
        playlistId
        contentType
        contentId
        playlistPosition
        playlist {
          playlistType
          playlistName
          id
        }
      }
    }
  }
`

/**
 * Update a playlist item's position.  Used by the Move Earlier / Move Later
 * controls on owned playlists.
 *
 * Semantic: `playlistPosition` is an integer; highest = top of list,
 * 0 = bottom.  With `reorderPlaylist: true` (schema default) the server
 * renormalizes other rows so positions stay dense.
 *
 * The Brooklyn resolver requires the row's stored `contentId` and
 * `contentType` even though the GraphQL schema marks them nullable —
 * same row-key pair every other playlist mutation uses.  Without those
 * fields the request fails with `"contentId" is required`.
 */
export const UPDATE_PLAYLIST_ITEM = gql`
  mutation UpdatePlaylistItem(
    $playlistItemId: ID!
    $contentId: Int!
    $contentType: String!
    $playlistPosition: Int!
    $playlistId: ID!
    $playlistType: String!
  ) {
    playlistUpdatePlaylistItem(
      playlistItem: {
        id: $playlistItemId
        contentId: $contentId
        contentType: $contentType
        playlistPosition: $playlistPosition
      }
      playlist: { id: $playlistId, playlistType: $playlistType }
      reorderPlaylist: true
    ) {
      playlistItem {
        id
        playlistId
        contentId
        contentType
        playlistPosition
      }
    }
  }
`

export const GET_COMMUNITY_LEADERBOARD = gql`
  query CommunityLeaderboard($timeWindow: LeaderboardTimeWindow, $limit: Int) {
    communityLeaderboard(timeWindow: $timeWindow, limit: $limit) {
      user {
        uid
      }
      reactionCount
    }
  }
`

export const GET_DISCOVER_PLAYLISTS = gql`
  query DiscoverPlaylists(
    $query: String!
    $limit: Int
    $after: String
    $visibility: [PlaylistVisibility]
    $order: PlaylistSearchOrder
  ) {
    playlistSearch(
      query: $query
      limit: $limit
      after: $after
      visibility: $visibility
      order: $order
    ) {
      edges {
        node {
          id
          playlistName
          playlistType
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_FOLLOWED_PLAYLISTS = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query FollowedPlaylists($uid: ID!, $limit: Int, $after: String, $language: [String]!) {
    followedPlaylists(uid: $uid, limit: $limit, after: $after) {
      edges {
        node {
          id
          playlistName
          playlistType
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
          }
          playlistItems {
            ...PlaylistItemContentFields
          }
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

// cache-and-network at the call site: engagement counts are eventually
// consistent across users, so a cached paint then a revalidate is fine.
// Backend bulk endpoint (gaia-brooklyn-api Query.playlistEngagement) takes
// a list of playlist IDs and returns one row each. Used to move
// followerCount off the shell into a hover / post-paint lookup.
export const PLAYLIST_ENGAGEMENT_BULK = gql`
  query PlaylistEngagementBulk($playlistIds: [ID!]!) {
    playlistEngagement(playlistIds: $playlistIds) {
      playlistId
      followerCount
    }
  }
`

// cache-and-network at the call site (own data — Community rule 7).
// Shell shape: same pattern as GET_MY_PLAYLISTS_SHELL — playlistItems carry
// only IDs; tile content is hydrated via GET_CONTENT_FOR_TILE_HYDRATION.
// userProfile is cheap with only { uuid, displayName } selected.
export const GET_FOLLOWED_PLAYLISTS_SHELL = gql`
  query FollowedPlaylistsShell($uid: ID!, $limit: Int = 24, $after: String, $searchQuery: String) {
    followedPlaylists(uid: $uid, limit: $limit, after: $after, searchQuery: $searchQuery) {
      edges {
        node {
          id
          playlistName
          playlistType
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
          }
          playlistItems {
            id
            contentId
            titleGroupId
            contentType
            playlistPosition
          }
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const SEARCH_MY_PLAYLISTS = gql`
  query SearchMyPlaylists($searchTerm: String!) {
    playlistList(userCurrent: true, includePrivate: true, searchTerm: $searchTerm) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        description
      }
    }
  }
`

export const GET_COMMENT_COUNT = gql`
  query ProfileCommentCount {
    commentList(userCurrent: true, limit: 1) {
      count
    }
  }
`

// The profile owner's OWN comments, newest first, for the Community Activity
// feed. Each comment federates the commented-on Content (thumbnail / title /
// path via MEDIA_TILE_CONTENT_FEATURED — the same selection `buildTileData`
// consumes) plus `commentParent`, the comment it replied to.
//
// `depthType` is parameterised so the caller fetches ROOT and REPLY pages
// separately and merges them: `CommentDepthType` is only `ROOT | REPLY` (no
// `ALL`), and the omitted default is not guaranteed to return both.
//
// `status` is selected DELIBERATELY and NOT filtered client-side — the whole
// point of the feed is that the author sees their own held comments badged.
// Brooklyn drops the status filter when `userCurrent: true`, so this is the
// only query in the app that returns non-PUBLISHED rows. Owner-only surface.
export const GET_PROFILE_COMMENTS = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query ProfileComments(
    $contentLanguage: [String]
    $limit: Int
    $offset: Int
    $depthType: CommentDepthType
  ) {
    commentList(
      userCurrent: true
      depthType: $depthType
      limit: $limit
      offset: $offset
      order: [["createdAt", "DESC"]]
    ) {
      comments {
        id
        pid
        contentId
        comment
        status
        createdAt
        userProfile {
          uuid
          displayName
          avatar { small }
        }
        userInfo { username }
        commentParent {
          id
          comment
          createdAt
          userProfile {
            uuid
            displayName
            avatar { small }
          }
          userInfo { username }
        }
        content {
          ...MediaTileContentFeatured
          # Deterministic image source, preferred over imageContentRelationships
          # (schema docs) — feeds getEpisodeThumbnail's assets-first branch so a
          # video with no d6-keyart-* relationship still gets a thumbnail.
          # Selected HERE, not in the shared fragment: MediaTileContentFeatured
          # was deliberately perf-trimmed (2026-05-11) and feeds five other
          # queries that render fine without it.
          contentAssetReferences(
            contentAssetReferences: [{ type: "keyart-no-text" }]
            limit: 1
          ) {
            asset { renderUrl }
          }
        }
      }
      count
    }
  }
`

// "Replies I received" — other members' replies to the owner's comments.
//
// There is no server-side "replies to my comments" operation, so this is
// expressed as `pid IN (<the owner's comment ids currently loaded>)`. That
// bound is real and deliberate: a reply to a comment on page 5 does not appear
// until page 5 is loaded. Making it exhaustive needs a Brooklyn query that does
// not exist and is explicitly out of scope — see the spec's §5.2a.
//
// NOT `userCurrent` — for OTHER members' rows the normal non-staff gate applies
// and only their PUBLISHED replies come back. That is correct: the owner must
// not see someone else's held reply. But Brooklyn's visibility predicate also
// returns the CALLER'S OWN rows regardless of status, so the caller's held
// replies come back from this read too — `buildCommentFeedEntries` drops rows
// authored by `viewerUuid` for exactly this reason.
export const GET_PROFILE_COMMENT_REPLIES = gql`
  ${MEDIA_TILE_CONTENT_FEATURED}
  query ProfileCommentReplies(
    $contentLanguage: [String]
    $parentIds: [ID]
    $limit: Int
  ) {
    commentList(
      where: { pid: { in: $parentIds } }
      limit: $limit
      order: [["createdAt", "DESC"]]
    ) {
      comments {
        id
        pid
        contentId
        comment
        status
        createdAt
        userProfile {
          uuid
          displayName
          avatar { small }
        }
        userInfo { username }
        content {
          ...MediaTileContentFeatured
          # Same deterministic image source as GET_PROFILE_COMMENTS — see the
          # comment there for why this lives outside the shared fragment.
          contentAssetReferences(
            contentAssetReferences: [{ type: "keyart-no-text" }]
            limit: 1
          ) {
            asset { renderUrl }
          }
        }
      }
      count
    }
  }
`

// Shell + hydrate: id-only item shells (via ...MemberPlaylistShellFields);
// each revealed row hydrates its own tiles per-row through `useRowHydration`.
// Offset-paginated. searchTerm narrows by playlistName server-side. No
// `$language` variable — the shell fetches no content, so content-language is
// applied per-row at hydration time instead. Kept `network-only` at the call
// site (member data is uncached — see the profile privacy rule).
export const GET_MEMBER_PLAYLISTS_SHELL = gql`
  ${MEMBER_PLAYLIST_SHELL_FIELDS}
  query GetMemberPlaylistsShell(
    $uid: ID!
    $visibility: [PlaylistVisibility!]!
    $limit: Int = 50
    $offset: Int = 0
    $searchTerm: String
  ) {
    playlistList(
      where: { uid: { eq: $uid } }
      visibility: $visibility
      order: [["updatedAt", "DESC"]]
      limit: $limit
      offset: $offset
      searchTerm: $searchTerm
    ) {
      count
      playlists {
        ...MemberPlaylistShellFields
      }
    }
  }
`

// ── Identity Vibe ─────────────────────────────────────────────────────

export const GENERATE_IDENTITY_VIBE = gql`
  mutation GenerateVibes($input: OracleIdentityVibeGenerateInput) {
    oracleIdentityVibeGenerate(input: $input) {
      success
      errors
      vibeLines
    }
  }
`

export const SELECT_IDENTITY_VIBE = gql`
  mutation SelectVibe($input: OracleIdentityVibeSelectInput!) {
    oracleIdentityVibeSelect(input: $input) {
      success
      errors
      oracleIdentityVibe {
        id
        vibe
        description
        generatedAt
      }
    }
  }
`

export const ORACLE_SEARCH_QUERY = gql`
  query OracleSearch(
    $input: OracleSearchInput!
    $language: [String]
    $learnerLevelLanguage: String!
  ) {
    oracleSearch(input: $input) {
      success
      errors
      executionTime
      text
      contentText
      playlistText
      content {
        contentId
        brooklynId
        contentType
        title
        score
        content {
          ...OracleContentFields
          seasonsCount(language: $language)
          episodesCount(language: $language, content: [{ publishedState: PUBLISHED_NOW }])
        }
      }
      playlists {
        id
        title
        score
        playlist {
          id
          playlistName
          visibility
          description
          itemCount
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
            avatar {
              url
              small
              card
            }
          }
          playlistItems {
            id
            contentId
            contentType
            playlistPosition
            content(language: $language) {
              ...OracleContentFields
            }
          }
        }
      }
    }
  }
  ${ORACLE_CONTENT_FIELDS}
`
